/**
 * PrivateRoute
 * ------------
 * Guards every protected route **and** silently patches the Firestore user
 * document the first time we notice that Auth says the address is verified
 * but Firestore still has emailVerified:false.
 *
 * • currentUser.reload()   → pulls fresh e‑mail‑verification state from Auth
 * • if (auth ✓) && (firestore ✗)   → updateDoc({ emailVerified:true })
 *
 * The rest of the component (status / role checks, loading spinners, etc.)
 * is unchanged.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Alert, Container, CircularProgress, Box,
} from '@mui/material';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';

const PrivateRoute = ({ allowedRoles }) => {
  const {
    currentUser,        // Firebase Auth user (may be null)
    userRole,           // role read from Firestore
    userStatus,         // approved / pending / denied
  } = useAuth();

  /* ─────────────────────────────────────────────────────────
     🔄 1. SYNC emailVerified BETWEEN Auth & Firestore
  ───────────────────────────────────────────────────────── */
  const [syncDone, setSyncDone] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');

  // Add timeout for loading states to prevent infinite grey screen
  useEffect(() => {
    if (currentUser && !userRole && !loadingTimeout) {
      const timer = setTimeout(() => {
        console.error('Loading timeout - user role not loaded after 10 seconds');
        setLoadingTimeout(true);
        setDebugInfo(`User: ${currentUser.email}, UID: ${currentUser.uid}, Email Verified: ${currentUser.emailVerified}`);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    }
  }, [currentUser, userRole, loadingTimeout]);

  useEffect(() => {
    /* nothing to do if there’s no logged‑in user */
    if (!currentUser) return;

    (async () => {
      try {
        /* make sure Auth is fresh (important when they just clicked the link) */
        await currentUser.reload();
        const freshUser = auth.currentUser;

        if (!freshUser.emailVerified || syncDone) return;     // already in sync

        const userRef  = doc(db, 'users', freshUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          if (userSnap.data().emailVerified !== true) {
            await updateDoc(userRef, { emailVerified: true });
            /* if the user was waiting for approval, AuthProvider will pick this
               up via onSnapshot and do the normal approve‑flow afterwards. */
          }
        } else {
          // User document doesn't exist but they have auth - create one
          console.log('Creating missing user document for authenticated user');
          await setDoc(userRef, {
            email: freshUser.email,
            emailVerified: true,
            status: 'pending',
            role: 'user',
            createdAt: new Date(),
            createdBy: 'private-route-recovery'
          });
        }
        setSyncDone(true);
      } catch (err) {
        console.error('Failed to sync emailVerified flag', err);
      }
    })();
  }, [currentUser, syncDone]);

  /* ─────────────────────────────────────────────────────────
     2. REDIRECT anonymous users
  ───────────────────────────────────────────────────────── */
  if (!currentUser) return <Navigate to="/login" />;

  /* ─────────────────────────────────────────────────────────
     3. STATUS‑gate (pending / denied)
  ───────────────────────────────────────────────────────── */
  if (userStatus === 'pending') {
    return (
      <Container>
        <Alert severity="warning">
          Your account is awaiting approval. Once you've verified your e‑mail
          address it’ll be approved shortly.
        </Alert>
      </Container>
    );
  }

  if (userStatus === 'denied') {
    return (
      <Container>
        <Alert severity="error">
          Your account has been denied. Please contact support if you believe
          this is a mistake.
        </Alert>
      </Container>
    );
  }

  /* ─────────────────────────────────────────────────────────
     4. WAIT while role is still loading (first snapshot not yet in)
  ───────────────────────────────────────────────────────── */
  if (currentUser && !userRole) {
    if (loadingTimeout) {
      return (
        <Container>
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>Account Loading Issue</strong>
            <br />
            We're having trouble loading your account data. This might be due to:
            <ul>
              <li>Incomplete registration process</li>
              <li>Network connectivity issues</li>
              <li>Account setup still in progress</li>
            </ul>
            <br />
            <strong>Debug Info:</strong> {debugInfo}
            <br /><br />
            Please try:
            <ol>
              <li>Refreshing this page</li>
              <li>Logging out and back in</li>
              <li>Contacting support if this persists</li>
            </ol>
          </Alert>
        </Container>
      );
    }
    
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Box ml={2}>Checking your access level, please wait…</Box>
        </Box>
      </Container>
    );
  }

  /* ─────────────────────────────────────────────────────────
     5. ROLE gate (if allowedRoles prop supplied)
  ───────────────────────────────────────────────────────── */
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return (
      <Container>
        <Alert severity="error">You are not authorized to access this page.</Alert>
      </Container>
    );
  }

  /* ─────────────────────────────────────────────────────────
     6. Everything ok → render the protected route
  ───────────────────────────────────────────────────────── */
  return <Outlet />;
};

export default PrivateRoute;
